/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { type QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type {
  GetReviewOptions,
  ReviewsResponse,
  ReviewsResponseType,
  ReviewType,
} from '../interface/types';
import { useMemo } from 'react';
import { isRetryableError } from '../utils/retryStrategies';

const URL = `${requestBaseURLGCPExtOrInt}/sls/product/product-reviews-integration-bs/v1/reviews`;
const STALE_TIME_5_MINUTES = 60000 * 5;

type ReviewsInfiniteQueryKey = readonly [
  'reviewsInfinite',
  GetReviewOptions & { enabled?: boolean }
];

export const selector = (apiReview: ReviewsResponse): ReviewsResponseType => {
  const productReviews = apiReview.productReviews.map((review) => ({
    reviewId: review.reviewId || '',
    productName: review.productName || '',
    reviewRating: review.reviewRating || 0,
    reviewTitle: review.reviewTitle || '',
    reviewerNickname: review.reviewerNickname || '',
    reviewText: review.reviewText || '',
    reviewerVehicle: review.reviewerVehicle || '',
    recommendedFlag: review.recommendedFlag || false,
    totalNegativeFeedbackCount: review.totalNegativeFeedbackCount || 0,
    totalPositiveFeedbackCount: review.totalPositiveFeedbackCount || 0,
    reviewSubmissionTimestamp: review.reviewSubmissionTimestamp || '',
    reviewResponseSubmissionTimestamp: review.reviewResponseSubmissionTimestamp || '',
    reviewResponseText: review.reviewResponseText || '',
    reviewResponseTeamName: review.reviewResponseTeamName || '',

    reviewPhotos: (review.reviewPhotos || []).map((photo) => ({
      photoCaption: photo.photoCaption || '',
      photoSizes: (photo.photoSizes || []).map((size) => ({
        photoSizeId: size.photoSizeId || 'normal',
        photoUrl: size.photoUrl || '',
      })),
    })),

    reviewSecondaryRatings: (review.reviewSecondaryRatings || []).map((rating) => ({
      ratingId: rating.ratingId || '',
      ratingValue: parseInt(rating.ratingValue || '0'),
      ratingDisplayLabel: rating.ratingDisplayLabel || '',
      ratingValueRange: parseInt(rating.ratingValueRange || '0'),
    })),

    reviewBadges: (review.reviewBadges || []).map((badge) => ({
      contentType: badge.contentType || '',
      badgeId: badge.badgeId || 'verifiedPurchaser',
      badgeType: badge.badgeType || '',
    })),
  }));

  return {
    totalReviews: apiReview.totalReviews,
    productReviews,
  };
};

const getReviews = async (
  options: GetReviewOptions,
  axiosInstance?: AxiosInstance
): Promise<ReviewsResponseType> => {
  const { skuNumber, ...queryParams } = options;
  const response = await getAxios(axiosInstance).get<ReviewsResponse>(URL, {
    params: {
      skuNumber,
      ...queryParams,
    },
    withCredentials: false,
  });
  return selector(response.data);
};

const { useData: useReviewData, prefetch: prefetchReviewData } = createQuery<
  ReviewsResponseType,
  GetReviewOptions
>('review', async (options, axiosInstance) => getReviews({ ...options }, axiosInstance));

type ReviewsOptions = GetReviewOptions & {
  enabled?: boolean;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
};

export const useGetReviews = <T = ReviewType>(
  options: ReviewsOptions & { select?: (data: ReviewsResponseType) => T }
) => {
  const { enabled = true } = options;
  return useReviewData({
    ...options,
    enabled,
    retry: isRetryableError,
    retryDelay: 1000,
  });
};

export const useGetReviewsInfinite = (options: GetReviewOptions & { enabled?: boolean }) => {
  const { enabled = true, skuNumber, limit = 10 } = options;

  const infiniteQueryResult = useInfiniteQuery({
    queryKey: ['reviewsInfinite', options] as ReviewsInfiniteQueryKey,
    queryFn: ({ pageParam }) => {
      return getReviews({
        ...options,
        offset: pageParam,
        limit,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.productReviews.length < limit) {
        return undefined;
      }

      const totalFetched = allPages.reduce((total, page) => total + page.productReviews.length, 0);
      return totalFetched;
    },
    enabled: Boolean(enabled && skuNumber),
    staleTime: STALE_TIME_5_MINUTES,
    retry: isRetryableError,
    retryDelay: 1000,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isPending,
    isLoading,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
  } = infiniteQueryResult;

  const mergedReviews = useMemo(() => {
    return data?.pages.flatMap((page) => page.productReviews) || [];
  }, [data]);

  const totalReviews = data?.pages[0]?.totalReviews || 0;

  return {
    data: mergedReviews,
    totalReviews,
    fetchNextPage: () => fetchNextPage().then(() => {}),
    hasNextPage,
    isFetching,
    isPending,
    isLoading,
    status,
    refetch: () => refetch().then(() => {}),
    fetchStatus,
    isSuccess,
    isError,
    infiniteQueryResult,
  };
};

export const prefetchReview = (
  options: GetReviewOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  return prefetchReviewData(queryClient, options, axiosInstance);
};

export const prefetchReviewsInfinite = async (
  options: GetReviewOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance
) => {
  const { limit = 20 } = options;

  await queryClient.prefetchInfiniteQuery({
    queryKey: ['reviewsInfinite', options] as ReviewsInfiniteQueryKey,
    queryFn: ({ pageParam }) => {
      return getReviews(
        {
          ...options,
          offset: pageParam,
          limit,
        },
        axiosInstance
      );
    },
    initialPageParam: 0,
  });
};
